import { useState, useEffect } from "react";
import { Menu, X, Phone, Facebook, Instagram } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV_LINKS = [
  { label: "Domov", href: "#domov" },
  { label: "O mne", href: "#o-mne" },
  { label: "Tréningy", href: "#treningy" },
  { label: "Cenník", href: "#cennik" },
  { label: "Recenzie", href: "#recenzie" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      data-testid="header"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#1C1C2E]/90 backdrop-blur-xl border-b border-white/10 shadow-2xl"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <button
            onClick={() => scrollTo("#domov")}
            data-testid="header-logo"
            className="text-left leading-none"
          >
            <span
              className="text-xl lg:text-2xl font-black tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              <span className="text-white">Danka </span>
              <span className="text-[#F72585]">Fitness</span>
            </span>
          </button>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
            {NAV_LINKS.map((link) => (
              <button
                key={link.label}
                onClick={() => scrollTo(link.href)}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className="text-sm font-medium text-zinc-300 hover:text-[#F72585] transition-colors duration-200"
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop Right */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:+421948848759"
              data-testid="header-phone"
              className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
            >
              <Phone size={14} />
              <span>+421 948 848 759</span>
            </a>
            <a
              href="https://www.facebook.com/dana.vysoka/"
              target="_blank"
              rel="noreferrer"
              data-testid="header-facebook"
              className="text-zinc-400 hover:text-[#F72585] transition-colors"
            >
              <Facebook size={18} />
            </a>
            <a
              href="https://www.instagram.com/sisticka6"
              target="_blank"
              rel="noreferrer"
              data-testid="header-instagram"
              className="text-zinc-400 hover:text-[#F72585] transition-colors"
            >
              <Instagram size={18} />
            </a>
            <button
              onClick={() => scrollTo("#kontakt")}
              data-testid="header-cta-button"
              className="px-5 py-2 bg-[#F72585] hover:bg-[#E01A4F] text-white text-sm font-semibold rounded-full transition-all duration-200 hover:scale-105"
            >
              Kontakt
            </button>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            data-testid="mobile-menu-button"
            className="lg:hidden p-2 text-white"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="mobile-menu"
            className="lg:hidden overflow-hidden bg-[#1C1C2E]/97 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-4 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.label}
                  onClick={() => scrollTo(link.href)}
                  className="text-left py-3 text-base font-medium text-zinc-200 hover:text-[#F72585] border-b border-white/5 transition-colors"
                >
                  {link.label}
                </button>
              ))}
              <a
                href="tel:+421948848759"
                className="flex items-center gap-2 text-sm text-zinc-400 py-3"
              >
                <Phone size={16} /> +421 948 848 759
              </a>
              <div className="flex gap-4 py-2">
                <a
                  href="https://www.facebook.com/dana.vysoka/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-zinc-400 hover:text-[#F72585]"
                >
                  <Facebook size={20} />
                </a>
                <a
                  href="https://www.instagram.com/sisticka6"
                  target="_blank"
                  rel="noreferrer"
                  className="text-zinc-400 hover:text-[#F72585]"
                >
                  <Instagram size={20} />
                </a>
              </div>
              <button
                onClick={() => scrollTo("#kontakt")}
                className="mt-2 py-3 bg-[#F72585] text-white font-semibold rounded-full"
              >
                Kontakt
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
